import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {Profile} from "../domain/profile.entity";
import {UpdateProfileParams, AvailabilityResult} from "../domain/profile.types";
import {createProfileRepository} from "../data/repositories";
import {GetUserProfileApiResponse} from "../../../react-query-hooks/profile/types";

// ==================== Query Keys ====================

export const PROFILE_QUERY_KEYS = {
  all: ["profile"] as const,
  detail: () => [...PROFILE_QUERY_KEYS.all, "detail"] as const,
  displayName: (name: string) =>
    [...PROFILE_QUERY_KEYS.all, "display-name", name] as const,
  username: (name: string) => [...PROFILE_QUERY_KEYS.all, "username", name] as const,
  displayNameStatus: () => [...PROFILE_QUERY_KEYS.all, "display-name-status"] as const,
};

// Legacy query key for cache compatibility
export const LEGACY_PROFILE_QUERY_KEY = "user_profile";

// ==================== Converters ====================

/**
 * Converts Profile entity to legacy GetUserProfileApiResponse shape.
 * Used by legacy-compatible hooks so 33+ consumers don't break.
 */
const toLegacyProfileResponse = (p: Profile): GetUserProfileApiResponse => ({
  avatar: p.avatar,
  cover_image: p.coverImage,
  behance: p.socialLinks.behance,
  buffer_time: p.bufferTime ?? undefined,
  country: p.country,
  createdAt: new Date(p.createdAt),
  description: p.description,
  discord: p.socialLinks.discord,
  display_name: p.displayName,
  dribble: p.socialLinks.dribble,
  email: p.email,
  facebook: p.socialLinks.facebook,
  firstName: p.firstName,
  github: p.socialLinks.github,
  how_did_you_find_us: p.howDidYouFindUs,
  id: p.id,
  instagram: p.socialLinks.instagram,
  isKyc_verified: p.isKycVerified,
  isSocialAuth: p.isSocialAuth,
  is_wallet_frozen: p.isWalletFrozen,
  lastName: p.lastName,
  linkedin: p.socialLinks.linkedin,
  shortBio: p.shortBio,
  statusId: p.statusId,
  statusName: p.statusName as "Active",
  suspension_reason: p.suspensionReason,
  tiktok: p.socialLinks.tiktok,
  twitch: p.socialLinks.twitch,
  twitter: p.socialLinks.twitter,
  updatedAt: new Date(p.updatedAt),
  userId: p.userId,
  username: p.username,
  wallet_freeze_reason: p.walletFreezeReason,
  website: p.socialLinks.website,
  youtube: p.socialLinks.youtube,
  twoFactorMethod: p.twoFactorMethod,
});

// ==================== Domain Hooks ====================

/**
 * Fetches the current user's profile as a Profile entity.
 * Uses staleTime: Infinity — profile data is manually invalidated after mutations.
 */
export const useGetProfile = () => {
  const repository = createProfileRepository();

  return useQuery<Profile, AxiosError<{status: boolean; message: string}>>({
    queryKey: PROFILE_QUERY_KEYS.detail(),
    queryFn: () => repository.getProfile(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation for updating the user's profile.
 * No optimistic updates — waits for server response, then updates cache.
 * Preserves analytics calls (Heap identify + Intercom update).
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const repository = createProfileRepository();

  return useMutation<Profile, AxiosError<{message: string}>, UpdateProfileParams>({
    mutationFn: (params) => repository.updateProfile(params),
    onSuccess: (profile) => {
      // Update new cache with server response
      queryClient.setQueryData(PROFILE_QUERY_KEYS.detail(), profile);
      // Update legacy cache so old consumers see the change immediately
      queryClient.setQueryData(
        [LEGACY_PROFILE_QUERY_KEY],
        toLegacyProfileResponse(profile)
      );
    },
    onError: (error) => {
      message.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: PROFILE_QUERY_KEYS.detail()});
      queryClient.invalidateQueries({queryKey: [LEGACY_PROFILE_QUERY_KEY]});
    },
  });
};

/**
 * Verifies display name availability.
 */
export const useVerifyDisplayName = (
  displayName: string,
  options?: {enabled: boolean}
) => {
  const repository = createProfileRepository();

  return useQuery<AvailabilityResult, AxiosError<{status: boolean; message: string}>>({
    queryKey: PROFILE_QUERY_KEYS.displayName(displayName),
    queryFn: () => repository.verifyDisplayName(displayName),
    enabled: !!displayName && displayName.length >= 3 && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/**
 * Verifies username availability.
 */
export const useVerifyUsername = (username: string) => {
  const repository = createProfileRepository();

  return useQuery<AvailabilityResult, AxiosError<{status: boolean; message: string}>>({
    queryKey: PROFILE_QUERY_KEYS.username(username),
    queryFn: () => repository.verifyUsername(username),
    enabled: username.length >= 3,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Checks whether the display name can be changed (14-day cooldown).
 */
export const useDisplayNameStatus = () => {
  const repository = createProfileRepository();

  return useQuery<AvailabilityResult, AxiosError>({
    queryKey: PROFILE_QUERY_KEYS.displayNameStatus(),
    queryFn: () => repository.checkDisplayNameStatus(),
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// ==================== Legacy-Compatible Hooks ====================

/**
 * Legacy-compatible hook for fetching the user profile.
 * Returns data in the old GetUserProfileApiResponse shape.
 *
 * @deprecated Use useGetProfile for new code.
 */
export const useGetUserProfile = () => {
  const repository = createProfileRepository();

  return useQuery<
    GetUserProfileApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [LEGACY_PROFILE_QUERY_KEY],
    queryFn: async () => {
      const profile = await repository.getProfile();
      return toLegacyProfileResponse(profile);
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Legacy-compatible mutation for updating the user profile.
 * Accepts the old UpdateUserProfileApiPayload shape.
 * Returns { data: GetUserProfileApiResponse } to match legacy callers.
 *
 * @deprecated Use useUpdateProfile for new code.
 */
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const repository = createProfileRepository();

  return useMutation<
    {data: GetUserProfileApiResponse},
    AxiosError<{message: string}>,
    import("../../../react-query-hooks/profile/types").UpdateUserProfileApiPayload
  >({
    mutationFn: async (payload) => {
      // Convert legacy payload to domain UpdateProfileParams
      const params: UpdateProfileParams = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        displayName: payload.display_name,
        shortBio: payload.shortBio,
        description: payload.shortDescription,
        country: payload.country,
        username: payload.username,
        howDidYouFindUs: payload.how_did_you_find_us,
        socialLinks: {
          twitter: payload.twitter,
          facebook: payload.facebook,
          instagram: payload.instagram,
          tiktok: payload.tiktok,
          discord: payload.discord,
          youtube: payload.youtube,
          dribble: payload.dribble,
          behance: payload.behance,
          twitch: payload.twitch,
          github: payload.github,
          website: payload.website,
          linkedin: payload.linkedin,
        },
      };

      const profile = await repository.updateProfile(params);
      const legacyResponse = toLegacyProfileResponse(profile);
      return {data: legacyResponse};
    },
    onSuccess: ({data}) => {
      // Update legacy cache
      queryClient.setQueryData([LEGACY_PROFILE_QUERY_KEY], data);
    },
    onError: (error) => {
      message.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: [LEGACY_PROFILE_QUERY_KEY]});
      queryClient.invalidateQueries({queryKey: PROFILE_QUERY_KEYS.detail()});
    },
  });
};

/**
 * Legacy-compatible display name verification.
 * Returns { isAvailable?: boolean } to match legacy shape.
 *
 * @deprecated Use useVerifyDisplayName from this module for new code.
 */
export const useLegacyVerifyDisplayName = (
  displayName: string,
  options?: {enabled: boolean}
) => {
  const repository = createProfileRepository();

  return useQuery<
    {isAvailable?: boolean},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["display-name", displayName],
    queryFn: async () => {
      const result = await repository.verifyDisplayName(displayName);
      return {isAvailable: result.isAvailable};
    },
    enabled: !!displayName && displayName.length >= 3 && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/**
 * Legacy-compatible username verification.
 * Returns { isAvailable?: boolean } to match legacy shape.
 *
 * @deprecated Use useVerifyUsername from this module for new code.
 */
export const useLegacyVerifyUsername = (username: string) => {
  const repository = createProfileRepository();

  return useQuery<
    {isAvailable?: boolean},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [LEGACY_PROFILE_QUERY_KEY, {username}],
    queryFn: async () => {
      const result = await repository.verifyUsername(username);
      return {isAvailable: result.isAvailable};
    },
    enabled: username.length >= 3,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Legacy-compatible display name status check.
 *
 * @deprecated Use useDisplayNameStatus from this module for new code.
 */
export const useLegacyDisplayNameStatus = () => {
  const repository = createProfileRepository();

  return useQuery<AvailabilityResult, AxiosError>({
    queryKey: ["display-name/status"],
    queryFn: () => repository.checkDisplayNameStatus(),
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// ==================== Exported Utilities ====================

export {toLegacyProfileResponse};
